/**
 * @fileoverview This file contains the code for the Header component.
 * It includes functionality for displaying the header section of the application.
 *
 * The Header component includes the following features:
 * - Displaying the application logo and navigation links
 * - Handling user interactions with the navigation links
 * - Displaying user information and options (e.g., theme switch, credits)
 * - Handling user interactions with launching lab, theme switch, and user options
 *
 * @copyright (C) 2024 qBraid Development Team
 */
/* eslint-disable react/no-unstable-nested-components */

import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	Alert,
	AppBar,
	Box,
	Button,
	IconButton,
	Snackbar,
	Stack,
	Typography
} from '@mui/material';
import {
	ArrowBack, TipsAndUpdatesOutlined
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useDispatch, useSelector } from 'react-redux';

import AcknowledgeNewTerms from '../acknowledgeNewTerms';
import CreditModal from '../creditModal';
import NewUserSurvey from '../newUserSurvey';
import { email, LAB_URL, ACCOUNT_URL } from '../../utils/axiosClient';
import {
	UserDataFunction,
	// UserDataFunction,
	UserLogoutFunction
} from '../../Redux/Action/userAction';
import {
	createCheckoutSession,
	getUserBilling,
	getUserCredits
} from '../../Redux/Action/billingAction';
import {
	switchToDarkTheme,
	switchToLightTheme
} from '../../Redux/Action/themeAction';
import { DARK_THEME, LIGHT_THEME } from '../../Redux/Types/themeTypes';
import { DRAWER_MAXIMIZE } from '../../Redux/Types/filesDrawerTypes';
import { halfFilesDrawer } from '../../Redux/Action/filesDrawerAction';
import { LoadComputeProfiles } from '../../Redux/Action/computeAction';
import { currentDomain } from '../../utils/domainBasedInfo/currentDomain';
import { getLabMaintenanceStatus } from '../../Redux/Action/maintenanceAction';
import { useLaunchLab } from '../common/useLaunchLab';
import NavigationDrawer from './components/NavigationDrawer';
import LaunchBar from './components/LaunchBar';
import NavigationBar from './components/NavigationBar';
import AccountMenuDropdown from './components/AccountMenuDropdown';
import './index.css';

/**
 * The header serves several purposes:
 * - It displays the application logo and navigation links
 * - It handles user interactions with the navigation links
 * - Handles launching lab, theme switch, and user options
 * @returns
 */
function Header() {
	const {
		handleLaunchLab, isSubmitting, handleCloseCredit, openCredit 
	} =
		useLaunchLab();

	const [anchorEl, setAnchorEl] = useState(null);
	const [activeNav, setActiveNav] = useState();
	const [drawerState, setDrawerState] = useState(false);
	const [getUserData, setGetUserData] = useState(false);
	const [openTermsModal, setOpenTermsModal] = useState(false);

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const billingSelector = useSelector((state) => state.billingReducer);
	const { userDetails } = useSelector((state) => state.userReducer);
	const { currentTheme } = useSelector((state) => state.themeReducer);

	const { drawerSize: filesDrawerSize } = useSelector(
		(state) => state.filesDrawerReducer
	);
	const initializeUserData = useCallback(() => {
		if (email) {
			dispatch(UserDataFunction(email));
			dispatch(getLabMaintenanceStatus());
		}
	}, [dispatch, email]);

	const handleNavigation = useCallback(
		(nav) => {
			setActiveNav(nav.name);
			if (nav?.name?.includes('Old version')) {
				window.location.href = ACCOUNT_URL;
			} else {
				navigate(nav.link);
			}
			handleClose();
			if (filesDrawerSize === DRAWER_MAXIMIZE) {
				dispatch(halfFilesDrawer());
			}
		},
		[navigate, dispatch, filesDrawerSize]
	);

	const handleLogout = useCallback(() => {
		handleClose();

		dispatch(UserLogoutFunction()).then(() => {
			window.open(`${LAB_URL}/hub/home?accountLogout`, '_blank');
		});

		Swal.fire({
			title: 'Logged out!',
			text: 'You have been successfully logged out.',
			confirmButtonColor: 'var(--brand-color-1)',
			inputAttributes: { id: 'confirm-logout' }
		}).then((_res) => {
			if (_res.isConfirmed) {
				handleNavigation(currentDomain.header.navigation[0]); // home navigation
				window.location.reload();
			}
		});
	}, [dispatch, handleNavigation]);

	useEffect(() => {
		initializeUserData();
	}, [initializeUserData]);

	useEffect(() => {
		if (pathname === '/') {
			setActiveNav('Your Plan');
		} else {
			const navItem =
				currentDomain.header.navigation.find(
					(item) =>
						pathname === item.link ||
						(pathname.startsWith(item.link) && item.link !== '/')
				) || currentDomain.header.navigation[0];
			if (navItem) setActiveNav(navItem.name);
		}
	}, [pathname]);

	useEffect(() => {
		if (email) {
			dispatch(getUserBilling()).then(() => dispatch(getUserCredits()));
			dispatch(LoadComputeProfiles());
		}
	}, [dispatch, email]);

	useEffect(() => {
		if (userDetails?.metadata?.newUser) {
			setGetUserData(true);
		}
	}, [userDetails?.metadata]);

	useEffect(() => {
		if (
			userDetails?.metadata &&
			!userDetails?.metadata?.acknowledgedTerms &&
			!['terms-and-conditions', 'privacy-policy', 'cookie-policy'].some(
				(path) => pathname.includes(path)
			)
		) {
			setOpenTermsModal(true);
		}
	}, [userDetails?.metadata]);

	const handleThemeSwitch = useCallback(
		(type) => {
			if (type === LIGHT_THEME) {
				dispatch(switchToLightTheme());
			} else if (type === DARK_THEME) {
				dispatch(switchToDarkTheme());
			}
		},
		[dispatch]
	);

	useEffect(() => {
		const storedTheme =
			JSON.parse(localStorage.getItem('qBraidTheme')) || LIGHT_THEME;
		if (storedTheme) {
			handleThemeSwitch(storedTheme);
		}
	}, [handleThemeSwitch]);

	useEffect(() => {
		localStorage.setItem('qBraidTheme', JSON.stringify(currentTheme));
	}, [currentTheme]);

	const handleBuyNow = useCallback(
		(priceId, mode) => {
			handleCloseCredit();
			dispatch(createCheckoutSession(priceId, mode));
		},
		[dispatch]
	);

	const handleMenu = (event) => setAnchorEl(event.currentTarget);
	const handleClose = () => setAnchorEl(null);
	const toggleDrawer = () => setDrawerState((prev) => !prev);

	useEffect(() => {
		const handleStorageChange = () => {
			const keys = Object.keys(localStorage);
			if (
				!keys.some((key) => key.includes('CognitoIdentityServiceProvider')) &&
				localStorage.getItem('logout')
			) {
				window.location.reload();
				localStorage.clear();
			}
		};

		window.addEventListener('storage', handleStorageChange);
		return () => window.removeEventListener('storage', handleStorageChange);
	}, []);

	return (
		<AppBar
			position="static"
			className="appBar-style"
			elevation={0}
			sx={(theme) => ({ backgroundColor: theme.palette.background.default })}
		>
			<Stack
				flexDirection="row"
				justifyContent="space-between"
				alignItems="center"
				width="100%"
			>
				<Box aria-describedby="home">
					<NavigationDrawer
						drawerState={drawerState}
						toggleDrawer={toggleDrawer}
						handleNavigation={handleNavigation}
					/>
					<IconButton
						title="Home"
						disableFocusRipple
						disableTouchRipple
						disableRipple
						onClick={() => handleNavigation(currentDomain.header.navigation[0])}
						sx={{ alignSelf: 'left' }}
						cy-data="header-logo"
					>
						<img
							src={
								currentTheme === LIGHT_THEME
									? currentDomain.logo_full.logo
									: currentDomain.logo_full.logo_dark
							}
							alt={currentDomain.title}
							className="appBar-logo reveal-animation"
						/>
					</IconButton>
				</Box>
				{!pathname.includes('subscription') && (
					<LaunchBar
						billing={billingSelector?.billing}
						handleLaunchLab={handleLaunchLab}
						isSubmitting={isSubmitting}
					/>
				)}
				<Stack
					flexDirection="row"
					alignItems="center"
					gap="1rem"
					paddingLeft={1}
				>
					<Typography
						fontFamily={['Inter', 'sans-serif']}
						fontSize={13}
						fontWeight={400}
						color="text.secondary"
						sx={{ display: {xs: 'none', sm: 'inline'}}}
					>
						{email}
					</Typography>
					<AccountMenuDropdown
						anchorEl={anchorEl}
						handleMenu={handleMenu}
						handleClose={handleClose}
						handleNavigation={handleNavigation}
						handleThemeSwitch={handleThemeSwitch}
						handleLogout={handleLogout}
						billingSelector={billingSelector}
					/>
				</Stack>
			</Stack>
			{!pathname.includes('subscription') ? (
				<NavigationBar
					activeNav={activeNav}
					currentTheme={currentTheme}
					handleNavigation={handleNavigation}
				/>
			) : (
				<Stack
					flex={1}
					flexDirection="row"
					alignItems="center"
					justifyContent="flex-start"
					alignSelf="flex-start"
				>
					<Button
						variant="outlined"
						color="inherit"
						size="small"
						startIcon={<ArrowBack />}
						sx={{ borderRadius: '1rem', color: 'grey' }}
						onClick={() => navigate(-1)}
						cy-data="sub-go-back"
					>
						Go Back
					</Button>
				</Stack>
			)}
			<NewUserSurvey
				isNewUser={getUserData}
				setGetUserData={setGetUserData}
				userDetails={userDetails}
			/>
			<AcknowledgeNewTerms
				openTermsModal={openTermsModal}
				setOpenTermsModal={setOpenTermsModal}
			/>
			<Snackbar
				anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
				open={isSubmitting}
				cy-data="header-snackbar"
			>
				<Alert
					iconMapping={{
						info: (
							<TipsAndUpdatesOutlined
								fontSize="inherit"
								color="inherit"
								sx={{ color: 'white' }}
							/>
						)
					}}
					severity="info"
					sx={(theme) => ({
						width: '100%',
						backgroundColor: theme.palette.primary.light,
						color: 'white'
					})}
					cy-data="header-snackbar-alert"
				>
					Please wait we are starting your server..
				</Alert>
			</Snackbar>
			{openCredit && (
				<CreditModal
					openCredit={openCredit}
					handleCloseCredit={handleCloseCredit}
					handleBuyNow={handleBuyNow}
				/>
			)}
		</AppBar>
	);
}

export default Header;
